import { type ReactElement, useEffect, useRef, useState } from "react"
import { useNavigate } from "react-router"
import { getUserManager } from "./userManager"

type CallbackState = "processing" | "failed"

/**
 * Completes the PKCE redirect and hands control back to the app. Rendered at
 * AUTH_CALLBACK_PATH; never reached when embedded in a host.
 *
 * Completing the redirect raises `userLoaded` on the shared manager, which is
 * what signs `AuthProvider` in — so the app below has the session by the time
 * this navigates home, with no reload.
 */
export const AuthCallback = (): ReactElement => {
  const navigate = useNavigate()
  const [state, setState] = useState<CallbackState>("processing")
  const started = useRef(false)

  useEffect(() => {
    // The authorization code is single-use; StrictMode's second mount would
    // redeem a code the first mount has already spent and report a failed
    // sign-in over a successful one.
    if (started.current) {
      return
    }

    started.current = true

    getUserManager()
      .signinRedirectCallback()
      .then(() => navigate("/", { replace: true }))
      .catch(() => setState("failed"))
  }, [navigate])

  if (state === "failed") {
    return (
      <p role="alert" data-qa-hook="auth-callback-error">
        Sign in could not be completed. Please try again.
      </p>
    )
  }

  return <p data-qa-hook="auth-callback-pending">Signing you in…</p>
}
